// deps
import React, { ReactElement, useCallback, useMemo, useState } from "react";

// internal-deps
import {
    BLAZE_EVENTS,
    CheckoutActionTypes,
    CheckoutState,
    DINEOUT_CHECKOUT_INITIAL_STATE,
    NEWRELIC_TABLES,
    recordNREvent,
    recordWebviewRefreshState,
    restoreWebviewRefreshState,
    useInit,
    useDisableCopyPaste,
} from "@swiggy-private/checkout-core";
import Blaze from "@swiggy-private/blaze.js";
import { getNRErrorInfo, isEmpty } from "@swiggy-private/js-utils";
import { NativeBridge } from "@swiggy-private/native-bridge";
import { SwgyHeadersKeys } from "@swiggy-private/checkout-core";
import { ErrorBoundary, LocalStorage, useEffectOnce } from "@swiggy-private/core-ui";
import { DineoutCheckoutRoute as CheckoutRoute } from "@swiggy-private/checkout-core";

// constants
import { CheckoutRouter } from "@dineout-cart-client/components/checkout-router";
import { ContextProvider } from "@dineout-cart-client/components/context-provider";
// utils
import { isReservationFlow, updateConfig } from "@dineout-cart-client/utils/TableReservationUtils";

// constants
import { DO_STORAGE_CART_KEY, IS_FROM_DINEOUT_CART } from "@dineout-cart-client/constants";
import { PAYLATER_TYPES } from "@swiggy-private/payment-types";

export interface DineoutCheckoutMainProps {
    config: {
        apiHost: string;
        paymentHost: string;
        paymentReturnUrl?: string;
        addMoneyReturnUrl?: string;
        analyticsHost: string;
        newrelicEndpoint?: string;
        newrelicKey?: string;
        environment?: string;
        simplPostLinkingRedirectUrl: string;
        enableWebviewRefreshCapability?: boolean;
    };
    headers: { [key in Partial<SwgyHeadersKeys>]: string };
    initialState?: {
        state?: DINEOUT_CHECKOUT_INITIAL_STATE;
        routeName?: CheckoutRoute;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        routeData?: any;
    };
    nativeBridgeSdk?: NativeBridge;
    /** SSR theme name from the HTML shell; forwarded to nested routes for SSR-safe theme resolution. */
    checkoutThemeName?: string | null;
}

export function DineoutCheckoutMain(props: DineoutCheckoutMainProps): ReactElement {
    const { config, headers, checkoutThemeName, initialState: checkoutState = {} } = props;
    const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";
    const _config = updateConfig(config);
    const isFromDineoutCart = isSessionStorageAvailable && sessionStorage.getItem(IS_FROM_DINEOUT_CART) === "true";

    // Checking if webview refresh happened and then restoring state
    const restoredWebviewRefreshState = useMemo(() => {
        const restoredState = restoreWebviewRefreshState(config?.enableWebviewRefreshCapability);

        try {
            // TODO: remove this temporary debug log once refresh issues are resolved
            recordNREvent({
                eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                name: "restoreWebviewRefreshStateInitialState",
                isUserLastStateNonEmpty: String(!isEmpty(restoredState)),
            });
        } catch {
            /* No-op */
        }

        return restoredState;
    }, [config?.enableWebviewRefreshCapability]);

    // As DO needs cart key to be set in the header for GPO to work, we are setting it here in headers if doesn't exist in headers by reading from local storage in which it was set before redicting to payments screen. This handling is needed in redirection scenarios and webview refresh scenarios where cart key is not set in headers on load of payments screen.
    const _headers = useMemo(() => {
        if (isEmpty(restoredWebviewRefreshState) || !isEmpty(headers.cartkey)) {
            return headers;
        }
        const cartKey = LocalStorage.getItem<string>(DO_STORAGE_CART_KEY);

        if (isEmpty(cartKey)) {
            return headers;
        }
        return {
            ...headers,
            cartkey: cartKey,
        };
    }, [headers, restoredWebviewRefreshState]);

    const [initialRoute] = useState(() => {
        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.CONFIRM_ORDER) {
            // added condition to push extra state in case of cart web view
            if (isFromDineoutCart) {
                window.history.pushState(
                    {
                        name: CheckoutRoute.PAYMENTS,
                        data: null,
                    },
                    "",
                    "",
                );
            }
            return {
                name: CheckoutRoute.CONFIRM_ORDER,
            };
        }

        if (
            checkoutState.state &&
            [
                DINEOUT_CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_FAILED,
                DINEOUT_CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER,
                DINEOUT_CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER,
                DINEOUT_CHECKOUT_INITIAL_STATE.PAYMENTS,
                DINEOUT_CHECKOUT_INITIAL_STATE.WALLET_MONEY_RECEIVED,
            ].includes(checkoutState.state)
        ) {
            return { name: CheckoutRoute.PAYMENTS };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.DINEOUT_SUCCESS_SCREEN) {
            return {
                name: CheckoutRoute.DINEOUT_PAYMENT_SUCCESS,
                routeData: checkoutState?.routeData,
                data: checkoutState.routeData,
            };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.BILL_PAYMENT_CART) {
            return {
                name: CheckoutRoute.BILL_PAYMENT_CART,
            };
        }

        if (isReservationFlow(checkoutState.state)) {
            return { name: CheckoutRoute.TABLE_RESERVATION_CART };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.STEAL_STORE_CART) {
            return { name: CheckoutRoute.STEAL_STORE_CART };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.SCENES_FULL_PAYMENT) {
            return {
                name: CheckoutRoute.SCENES_FULL_PAYMENT,
                data: checkoutState.routeData,
            };
        }

        if (checkoutState.routeName) {
            return { name: checkoutState.routeName, data: checkoutState.routeData };
        }

        return { name: CheckoutRoute.CART };
    });

    const [initialState] = useState<CheckoutState>((): CheckoutState => {
        if (!isEmpty(restoredWebviewRefreshState)) {
            return restoredWebviewRefreshState;
        }
        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.WALLET_MONEY_RECEIVED) {
            // added condition to push extra state in case of cart web view
            if (isFromDineoutCart) {
                window.history.pushState({ name: CheckoutRoute.PAYMENTS, data: null }, "", "");
            }
            return { type: CheckoutActionTypes.ADD_MONEY_CONFIRM };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.BILL_PAYMENT_CART) {
            return { type: CheckoutActionTypes.DINEOUT_BILL_PAYMENT_CART };
        }

        if (isReservationFlow(checkoutState.state)) {
            return {
                type: CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART,
                payload: {
                    isEventBookingFlow: checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.EVENT_BOOKING_CART,
                },
            };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_FAILED) {
            return {
                type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED,
                payload: {
                    error: true,
                    paymentCode: PAYLATER_TYPES.SIMPL,
                },
            };
        }
        if (
            checkoutState.state ===
            DINEOUT_CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER
        ) {
            return {
                type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED,
                payload: {
                    paymentCode: PAYLATER_TYPES.SIMPL,
                },
            };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER) {
            return {
                type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER,
                payload: {
                    paymentCode: PAYLATER_TYPES.SIMPL,
                },
            };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.PAYMENTS) {
            return { type: CheckoutActionTypes.SHOW_PAYMENTS };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.DINEOUT_SUCCESS_SCREEN) {
            return { type: CheckoutActionTypes.DINEOUT };
        }
        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.CONFIRM_ORDER) {
            return {
                type: CheckoutActionTypes.CONFIRM_ORDER,
                payload: null,
            };
        }

        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.SCENES_FULL_PAYMENT) {
            return {
                type: CheckoutActionTypes.SCENES_FULL_PAYMENT,
            };
        }
        if (checkoutState.state === DINEOUT_CHECKOUT_INITIAL_STATE.STEAL_STORE_CART) {
            return { type: CheckoutActionTypes.STEAL_STORE_CART };
        }

        // set correct default
        return { type: CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART };
    });

    useInit({
        headers: _headers,
        nbSdk: props.nativeBridgeSdk,
        newrelicEndpoint: config.newrelicEndpoint,
        newrelicKey: config.newrelicKey,
        environment: config.environment,
    });

    useDisableCopyPaste();

    useEffectOnce(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.DINEOUT_CHECKOUT_WEB_VIEW,
            state: checkoutState.state || "--",
        });
        recordWebviewRefreshState(config?.enableWebviewRefreshCapability);
        // record the application start time, used for UXZ metrics
        // (time from navigation start till application router are ready to serve the routes)
        Blaze.recordTimeFromAppStart(BLAZE_EVENTS.RECORD_DINEOUT_APP_LOAD_TIME, {
            initialState: checkoutState.state || "--",
        });

        if (initialRoute.name === CheckoutRoute.TABLE_RESERVATION_CART) {
            // start marker for cart page load time
            Blaze.recordStart(BLAZE_EVENTS.RECORD_TABLE_BOOKING_CART_LOAD_TIME);
        } else if (initialRoute.name === CheckoutRoute.BILL_PAYMENT_CART) {
            Blaze.recordStart(BLAZE_EVENTS.RECORD_BILL_PAYMENT_CART_LOAD_TIME);
        } else if (initialRoute.name === CheckoutRoute.STEAL_STORE_CART) {
            Blaze.recordStart(BLAZE_EVENTS.RECORD_STEAL_STORE_CART_LOAD_TIME);
        }
    });

    const onError = useCallback(
        (error?: Error, errorInfo?: React.ErrorInfo) => {
            const errorEvents = getNRErrorInfo(error, errorInfo);

            recordNREvent({
                eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
                state: checkoutState.state || "--",
                ...errorEvents,
            });
        },
        [checkoutState.state],
    );

    return (
        <ErrorBoundary errorFallback={onError}>
            <ContextProvider
                config={_config}
                initialState={initialState}
                headers={headers}
                checkoutThemeName={checkoutThemeName}
            >
                <CheckoutRouter initialRoute={initialRoute}></CheckoutRouter>
            </ContextProvider>
        </ErrorBoundary>
    );
}
